import {
  Autocomplete,
  SearchMd,
  boxShadow,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import debounce from 'lodash/debounce';
import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { usePOIAutocompleteSessionToken } from '../../../../../app-kit/hooks/usePOIAutocompleteSessionToken';
import { fetchPOISuggestions } from '../../helpers/fetchPOISuggestions';
import type { SuggestionType } from '../../helpers/getPointOfInterestSuggestionType';
import { mapAutocompleteOptionToPointOfInterestSuggestListNode } from '../../helpers/mapAutocompleteOptionToPointOfInterestSuggestListNode';
import { InputPill } from '../InputPill/InputPill';

const dangerouslySetClassNames = {
  scrollableArea: 'poi-suggest-scrollable-area',
  listbox: 'poi-suggest-listbox',
  listboxWrapper: 'poi-suggest-listbox-wrapper',
  input: 'poi-suggest-input',
  inputBorder: 'poi-suggest-input-border',
  iconWrapper: 'poi-suggest-icon-wrapper',
  listboxOption: 'poi-suggest-listbox-option',
};

export interface Option {
  value: string;
  suggestionText: string;
  suggestionType: SuggestionType;
  selectedText: string;
}

const StyledAutocomplete = styled(Autocomplete)`
  .poi-suggest-input {
    &::placeholder {
      ${typographyHelper('body01')};
      color: ${paletteHelper('textSecondary')};
    }
  }

  .poi-suggest-input-border:focus-within::after {
    border: 2px solid ${paletteHelper('borderHovered')};
    border-radius: ${radiusHelper('medium')};
  }

  .poi-suggest-scrollable-area {
    max-height: 7.875rem;
    padding-top: ${spacingHelper('small')};
    padding-bottom: ${spacingHelper('twoExtraSmall')};
  }

  .poi-suggest-listbox-wrapper {
    margin-top: ${spacingHelper('extraSmall')};
  }

  .poi-suggest-listbox {
    border: 0;
    ${boxShadow.defaultBoxShadow}
    border-radius: ${radiusHelper('medium')};
    max-height: none;
  }

  li {
    cursor: pointer;
    &:not(:last-child) {
      & > div {
        border-bottom: 1px solid ${paletteHelper('borderSecondary')};
      }
    }
  }
`;

interface PointOfInterestSuggestProps {
  label: string;
  placeholder: string;
  noResultsMessage: string;
  selectedOption: Omit<Option, 'suggestionType'> | null;
  onSelectedOptionChange: (option: Option | null) => void;
  id?: string;
}

export const PointOfInterestSuggest: FunctionComponent<PointOfInterestSuggestProps> = ({
  label,
  placeholder,
  noResultsMessage,
  selectedOption,
  onSelectedOptionChange,
  id,
}) => {
  const [pointOfInterestSuggestions, setPointOfInterestSuggestions] = useState<Option[]>([]);
  const [noResults, setNoResults] = useState(false);
  const { sessionToken, setPOISessionToken } = usePOIAutocompleteSessionToken();

  useEffect(() => {
    if (!sessionToken) {
      setPOISessionToken();
    }
  }, [sessionToken, setPOISessionToken]);

  const fetchPointOfInterestSuggestions = (inputValue: string): void => {
    fetchPOISuggestions(inputValue, sessionToken ?? '')
      .then((fetchedSuggestions) => {
        if (!fetchedSuggestions) {
          setNoResults(true);
        } else {
          setNoResults(fetchedSuggestions.length === 0);
          setPointOfInterestSuggestions(fetchedSuggestions);
        }
      })
      .catch(() => {});
  };

  const debouncedFetchPointOfInterestSuggestions = debounce(fetchPointOfInterestSuggestions, 200);

  const onPOIInputChange = (inputValue: string): void => {
    if (inputValue) {
      debouncedFetchPointOfInterestSuggestions(inputValue);
    } else {
      debouncedFetchPointOfInterestSuggestions.cancel();
      setPointOfInterestSuggestions([]);
      setNoResults(false);
    }
  };

  const onClear = (): void => {
    onSelectedOptionChange(null);
    setPointOfInterestSuggestions([]);
    setNoResults(false);
  };

  return selectedOption ? (
    <InputPill onClear={onClear} label={selectedOption.selectedText} />
  ) : (
    <StyledAutocomplete
      id={id}
      sizeVariant="large"
      autoFocus
      openOnFocus
      showOptionsOnMatch
      label={label}
      hideLabel
      placeholder={placeholder}
      onInputChange={onPOIInputChange}
      options={pointOfInterestSuggestions}
      mapOptionToString={(_option: Option) => _option.suggestionText}
      mapOptionToListNode={mapAutocompleteOptionToPointOfInterestSuggestListNode}
      filterOptions={(options: Option[]) => options}
      onSelectedOptionChange={onSelectedOptionChange}
      dangerouslySetClassNames={dangerouslySetClassNames}
      noResults={noResults}
      noResultsMessage={noResultsMessage}
      iconRight={<SearchMd />}
    />
  );
};
